import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaChevronRight, FaCloudUploadAlt, FaFolder, FaSearch } from "react-icons/fa";
import {
  closeEmployeeFolder,
  deleteEmployeeDocument,
  downloadEmployeeDocument,
  fetchEmployeeDocumentVersions,
  fetchEmployeeFolder,
  fetchEmployeeFolders,
  uploadEmployeeDocument,
  uploadEmployeeDocumentVersion,
} from "../../redux/slices/organizationDocumentsSlice";
import { EmptyState, FileTable, FolderGrid, SectionTitle, VersionDialog, scopeIcon } from "./DocumentViews";

const RANK = { view: 1, edit: 2, manage: 3 };
const ACCEPTED_FILES = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.rtf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp";
export const atLeast = (permission, level) => RANK[permission] >= RANK[level];

/**
 * One employee's documents file: folder cards, then the files of the opened folder.
 * Used on the employee profile and as "My documents" on the Documents page.
 */
const EmployeeDocumentsPanel = ({ userId, rootLabel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uploadInput = useRef(null);
  const versionInput = useRef(null);
  const versionTarget = useRef(null);
  const [search, setSearch] = useState("");
  const [versionDocument, setVersionDocument] = useState(null);
  const {
    employeeFolders,
    employeeFolder,
    employeeDocuments,
    employeeLoading,
    employeeError,
    versionsByDocument,
  } = useSelector((state) => state.organizationDocuments);

  const tr = (key, options) => t(`components.employeeProfile.documentsTab.${key}`, options);
  const canUpload = Boolean(employeeFolder && atLeast(employeeFolder.permission, "edit"));

  useEffect(() => {
    dispatch(closeEmployeeFolder());
    dispatch(fetchEmployeeFolders(userId));
  }, [dispatch, userId]);

  useEffect(() => setSearch(""), [employeeFolder?.id]);

  const openFolder = (folderId) => dispatch(fetchEmployeeFolder({ userId, folderId }));
  const goRoot = () => dispatch(closeEmployeeFolder());
  const refresh = () => {
    dispatch(fetchEmployeeFolders(userId));
    if (employeeFolder) openFolder(employeeFolder.id);
  };

  const run = async (action, successKey, reload = true) => {
    try {
      await dispatch(action).unwrap();
      if (successKey) toast.success(tr(successKey));
      if (reload) refresh();
    } catch (error) {
      toast.error(error?.message || tr("genericError"));
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    for (const file of files) {
      await run(uploadEmployeeDocument({ userId, folderId: employeeFolder.id, file }), "uploaded", false);
    }
    refresh();
  };

  const handleVersion = (event) => {
    const [file] = event.target.files || [];
    event.target.value = "";
    if (file && versionTarget.current) {
      run(uploadEmployeeDocumentVersion({ userId, documentId: versionTarget.current, file }), "versionUploaded");
    }
  };

  const openVersions = (document) => {
    setVersionDocument(document);
    run(fetchEmployeeDocumentVersions({ userId, documentId: document.id }), null, false);
  };

  const handleDelete = (document) => {
    if (window.confirm(tr("confirmDelete", { name: document.title }))) {
      run(deleteEmployeeDocument({ userId, documentId: document.id }), "deleted");
    }
  };

  const shownFolders = useMemo(
    () => employeeFolders.filter((folder) => folder.name.toLowerCase().includes(search.toLowerCase())),
    [employeeFolders, search],
  );
  const shownDocuments = useMemo(
    () => employeeDocuments.filter((document) => document.title.toLowerCase().includes(search.toLowerCase())),
    [employeeDocuments, search],
  );

  return (
    <section className="document-browser">
      <div className="document-browser-toolbar">
        <nav className="document-breadcrumbs" aria-label={t("pages.Documents.breadcrumbs")}>
          <button onClick={goRoot}>{rootLabel || t("pages.Documents.documents")}</button>
          {employeeFolder && <span><FaChevronRight /><button onClick={() => openFolder(employeeFolder.id)}>{employeeFolder.name}</button></span>}
        </nav>
        <div className="document-toolbar-actions">
          <label className="document-search">
            <FaSearch />
            <span className="visually-hidden">{t("pages.Documents.search")}</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("pages.Documents.searchPlaceholder")} />
          </label>
          {canUpload && (
            <button className="document-button document-button--primary" onClick={() => uploadInput.current?.click()}>
              <FaCloudUploadAlt />{t("pages.Documents.uploadFiles")}
            </button>
          )}
          <input ref={uploadInput} hidden multiple type="file" accept={ACCEPTED_FILES} onChange={handleUpload} data-testid="employee-documents-upload" />
          <input ref={versionInput} hidden type="file" accept={ACCEPTED_FILES} onChange={handleVersion} />
        </div>
      </div>

      {employeeError && <div className="document-error" role="alert">{employeeError}</div>}
      {employeeLoading ? <div className="document-loading">{t("common.actions.loading")}</div> : !employeeFolder ? (
        <>
          <SectionTitle title={t("pages.Documents.folders")} count={shownFolders.length} />
          {shownFolders.length ? (
            <FolderGrid
              folders={shownFolders}
              variant="employee"
              onOpen={(folder) => openFolder(folder.id)}
              renderMeta={(folder) => (
                <>
                  <span>{tr("fileCount", { count: folder.document_count })}</span>
                  <span>{t(`pages.Documents.permission_${folder.permission}`)}</span>
                </>
              )}
            />
          ) : <EmptyState icon={<FaFolder />} title={tr("noFolders")} hint="" />}
        </>
      ) : (
        <>
          <div className="document-folder-heading">
            <div>
              <span>{scopeIcon.employee}</span>
              <div><h2>{employeeFolder.name}</h2><p>{employeeFolder.description || t("pages.Documents.noDescription")}</p></div>
            </div>
          </div>
          <SectionTitle className="document-files-heading" title={t("pages.Documents.files")} count={shownDocuments.length} />
          {shownDocuments.length ? (
            <FileTable
              documents={shownDocuments}
              onDownload={(document) => run(downloadEmployeeDocument({ userId, documentId: document.id }), null, false)}
              onVersions={openVersions}
              onNewVersion={(document) => { versionTarget.current = document.id; versionInput.current?.click(); }}
              onDelete={handleDelete}
              canChange={(document) => atLeast(document.permission, "edit")}
              canDelete={(document) => atLeast(document.permission, "manage")}
              renderNote={(document) => document.shared && tr("sharedWithEveryone")}
            />
          ) : (
            <EmptyState
              className="document-empty--files"
              icon={<FaCloudUploadAlt />}
              title={t("pages.Documents.noFiles")}
              hint={canUpload ? t("pages.Documents.noFilesHint") : t("pages.Documents.noFilesReadOnly")}
            />
          )}
        </>
      )}

      {versionDocument && (
        <VersionDialog
          document={versionDocument}
          versions={versionsByDocument[versionDocument.id] || []}
          onClose={() => setVersionDocument(null)}
          onDownload={(versionId) => run(downloadEmployeeDocument({ userId, documentId: versionDocument.id, versionId }), null, false)}
        />
      )}
    </section>
  );
};

export default EmployeeDocumentsPanel;
